import {
  Component,
  ElementRef,
  ViewChild,
  OnDestroy,
  Inject,
  PLATFORM_ID,
  inject,
  afterNextRender,
  ChangeDetectionStrategy,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LocalizationService } from '@services/logic/localization.service';

@Component({
  selector: 'app-trustpilot-widget',
  templateUrl: './trustpilot-widget.component.html',
  styleUrls: ['./trustpilot-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { ngSkipHydration: 'true' },
})
export class TrustpilotWidgetComponent implements OnDestroy {
  @ViewChild('trustpilotWidget', { static: false })
  trustpilotWidget?: ElementRef<HTMLDivElement>;
  private readonly localizationService = inject(LocalizationService);

  /** Business unit, review page and language come from the market config. */
  protected readonly profile = this.localizationService.trustpilotProfile;
  protected readonly locale = this.localizationService.bcp47;

  public trustpilotInitialized = false;
  private isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    // Only initialize on browser platform
    afterNextRender(() => {
      this.initializeTrustpilotWidget();
    });
  }

  private initializeTrustpilotWidget(): void {
    if (
      this.trustpilotWidget?.nativeElement &&
      typeof window !== 'undefined' &&
      (window as any).Trustpilot &&
      !this.trustpilotInitialized
    ) {
      try {
        (window as any).Trustpilot.loadFromElement(
          this.trustpilotWidget.nativeElement,
          true,
        );
        this.trustpilotInitialized = true;
      } catch (error) {
        console.warn('Error initializing Trustpilot widget:', error);
      }
    }
    if (!this.trustpilotWidget?.nativeElement) {
      this.trustpilotInitialized = false;
    }
  }

  /**
   * Manually re-initialize the Trustpilot widget
   */
  public reinitialize(): void {
    this.trustpilotInitialized = false;
    if (this.isBrowser) {
      this.initializeTrustpilotWidget();
    }
  }

  ngOnDestroy(): void {
    // Reset the initialization flag when component is destroyed
    this.trustpilotInitialized = false;

    // Clean up any existing Trustpilot widgets only on browser
    if (
      this.isBrowser &&
      typeof window !== 'undefined' &&
      (window as any).Trustpilot
    ) {
      try {
        // Remove any existing Trustpilot widgets from the element
        if (this.trustpilotWidget?.nativeElement) {
          const existingWidgets =
            this.trustpilotWidget.nativeElement.querySelectorAll(
              '[data-template-id]',
            );
          existingWidgets.forEach((widget) => {
            widget.remove();
          });
        }
      } catch (error) {
        console.warn('Error cleaning up Trustpilot widget:', error);
      }
    }
  }
}
